import { useRef } from "react";

interface CertificateProps {
  studentName: string;
  courseName: string;
  instructorName: string;
  completionDate: string;
  certificateId: string;
}

export default function Certificate({
  studentName,
  courseName,
  instructorName,
  completionDate,
  certificateId,
}: CertificateProps) {
  const certificateRef = useRef<HTMLDivElement>(null);

  const downloadPDF = async () => {
    // ใช้ html2canvas + jsPDF
    const html2canvas = (await import("html2canvas")).default;
    const jsPDF = (await import("jspdf")).default;

    if (certificateRef.current) {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 297; // A4 landscape width
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`certificate-${certificateId}.pdf`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Certificate Preview */}
      <div
        ref={certificateRef}
        className="bg-white p-16 rounded-2xl shadow-large border-8 border-double border-primary relative overflow-hidden"
        style={{ aspectRatio: "1.414/1" }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-primary rounded-full blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center h-full flex flex-col justify-between">
          {/* Header */}
          <div>
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
                <span className="text-white font-bold text-4xl">E</span>
              </div>
            </div>
            <h1 className="text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
              ใบประกาศนียบัตร
            </h1>
            <p className="text-lg text-textLight">Certificate of Completion</p>
          </div>

          {/* Body */}
          <div className="my-8">
            <p className="text-xl text-textLight mb-4">ขอแสดงความยินดีกับ</p>
            <h2 className="text-4xl font-bold text-textDark mb-8 border-b-2 border-primary inline-block pb-2 px-8">
              {studentName}
            </h2>
            <p className="text-xl text-textLight mb-4">ที่ได้เรียนจบคอร์ส</p>
            <h3 className="text-3xl font-bold text-primary mb-8">{courseName}</h3>
            <p className="text-lg text-textLight">
              ภายใต้การสอนของ{" "}
              <span className="font-semibold text-textDark">{instructorName}</span>
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-end">
            <div className="text-left">
              <p className="text-sm text-textLight">วันที่สำเร็จการศึกษา</p>
              <p className="font-semibold text-textDark">{completionDate}</p>
            </div>
            <div className="text-right">
              <div className="border-t-2 border-textDark w-48 mb-2"></div>
              <p className="font-semibold text-textDark">{instructorName}</p>
              <p className="text-sm text-textLight">ผู้สอน</p>
            </div>
          </div>

          {/* Certificate ID */}
          <div className="mt-4">
            <p className="text-xs text-textLight">
              Certificate ID: {certificateId}
            </p>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="text-center mt-8">
        <button
          onClick={downloadPDF}
          className="bg-gradient-primary hover:shadow-large text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
        >
          📥 ดาวน์โหลดใบเซอร์ (PDF)
        </button>
      </div>
    </div>
  );
}
